import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import Logo from "../components/Logo";
import { motion } from "framer-motion";
import { getReferralData } from "@/utils/referralTracking";

// Google Icon SVG component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const businessTypes = [
  { value: "consulting", label: "Consulting" },
  { value: "agency", label: "Agency (Marketing, Creative, etc.)" },
  { value: "legal", label: "Legal Services" },
  { value: "accounting", label: "Accounting / Finance" },
  { value: "coaching", label: "Coaching / Training" },
  { value: "technology", label: "Technology / Software" },
  { value: "healthcare", label: "Healthcare / Wellness" },
  { value: "real_estate", label: "Real Estate" },
  { value: "home_services", label: "Home Services" },
  { value: "other", label: "Other Professional Services" }
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    business_name: "",
    business_type: ""
  });

  // Referral capture is now handled universally in App.js
  // No need to capture here - just read from localStorage on submit

  // Redirect if already logged in - go to complete-signup to check billing status
  useEffect(() => {
    if (user) {
      navigate("/complete-signup");
    }
  }, [user, navigate]);

  // Don't render form if user is logged in
  if (user) {
    return null;
  }

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    
    try {
      // Get referral data from localStorage (captured universally)
      const refData = getReferralData();
      
      await register(
        formData.email,
        formData.password,
        formData.business_name,
        formData.business_type || "other",
        refData.referral_code,
        refData.utm_source,
        refData.utm_campaign
      );
      toast.success("Account created! Choose your plan to get started.");
      // Redirect to complete-signup for plan selection and payment
      navigate("/complete-signup");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex">
      {/* Left side - Visual */}
      <div className="hidden lg:flex flex-1 bg-primary items-center justify-center p-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center text-white max-w-md"
        >
          <h2 className="text-4xl font-bold mb-4" >
            Start Converting More Leads Today
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Create your custom intake form in minutes and let AI handle the lead qualification.
          </p>
          <div className="space-y-4 text-left">
            {[
              "Custom intake forms tailored to your business",
              "AI-powered lead scoring and prioritization",
              "Instant email notifications for new leads",
              "7-day free trial, up to 20 submissions"
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm font-medium">
                  {i + 1}
                </div>
                <span className="text-sm">{item}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
      
      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          
          <Card className="rounded-2xl border-none shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
            <CardHeader className="space-y-1 pb-4">
              <div className="flex items-center gap-3 mb-2">
                <Logo size={40} className="text-primary" />
                <span className="text-xl font-semibold" >HeyIntake</span>
              </div>
              <CardTitle className="text-2xl" >Create your account</CardTitle>
              <CardDescription>Start your 7-day free trial</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="business_name">Business Name</Label>
                  <Input
                    id="business_name"
                    type="text"
                    placeholder="Acme Services"
                    value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                    required
                    className="h-12"
                    data-testid="register-business-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="business_type">Business Type</Label>
                  <Select
                    value={formData.business_type}
                    onValueChange={(value) => setFormData({ ...formData, business_type: value })}
                  >
                    <SelectTrigger className="h-12" data-testid="register-business-type-select">
                      <SelectValue placeholder="Select your industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {businessTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="h-12"
                    data-testid="register-email-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      className="h-12"
                      data-testid="register-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      required
                      className="h-12"
                      data-testid="register-confirm-password-input"
                    />
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full rounded-full h-12"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    "Create Account"
                  )}
                </Button>
              </form>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-muted-foreground">Or continue with</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full rounded-full h-12 flex items-center justify-center gap-3"
                onClick={handleGoogleSignup}
                data-testid="google-signup-btn"
              >
                <GoogleIcon />
                <span>Continue with Google</span>
              </Button>
              
              <p className="text-center text-sm text-muted-foreground mt-6">
                Already have an account?{" "}
                <Link to="/login" className="text-primary font-medium hover:underline" data-testid="register-login-link">
                  Sign in
                </Link>
              </p>
              {/* Build version for debugging */}
              <p className="text-center text-xs text-muted-foreground/50 mt-4" data-testid="build-version">Build: v1.1.0-trial7day</p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
